import React from 'react';

/**
 * UserFilters Component
 * @param {boolean} embedded - When true, renders without card wrapper (for use in bottom sheet/dropdown)
 * @param {boolean} hideSearch - When true, hides search input (search is in sticky bar)
 * @param {boolean} hideHeader - When true, hides the header with Clear All button
 */
function UserFilters({
  filters,
  onFilterChange,
  leaders,
  managers,
  sids,
  embedded = false,
  hideSearch = false,
  hideHeader = false
}) {
  function handleChange(e) {
    const { name, value } = e.target;
    onFilterChange({
      ...filters,
      [name]: value
    });
  }

  function handleClearFilters() {
    onFilterChange({
      role: '',
      leaderId: '',
      managerId: '',
      sidId: '',
      search: filters.search // Keep search when clearing other filters
    });
  }

  // Check if any supervisor filter should be shown based on selected role
  const showLeaderFilter = !filters.role || ['agent', 'telemarketer'].includes(filters.role);
  const showManagerFilter = !filters.role || filters.role === 'leader';
  const showSidFilter = !filters.role || ['asid', 'designer', 'draughter_2d'].includes(filters.role);

  // Wrapper class based on embedded mode
  const wrapperClass = embedded ? '' : 'card mb-6';

  return (
    <div className={wrapperClass}>
      {!hideHeader && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-display font-semibold">Filters</h3>
          <button
            onClick={handleClearFilters}
            className="text-sm text-tenq-gold hover:text-tenq-gold-dark transition-colors"
          >
            Clear All
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Search - hide when search is in sticky bar */}
        {!hideSearch && (
          <div>
            <label className="label-sm">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleChange}
              className="input"
              placeholder="Name or email..."
            />
          </div>
        )}

        {/* Role Filter */}
        <div>
          <label className="label-sm">Role</label>
          <select
            name="role"
            value={filters.role}
            onChange={handleChange}
            className="input"
          >
            <option value="">All Roles</option>
            <optgroup label="Management">
              <option value="admin">Admin</option>
              <option value="director">Director</option>
              <option value="manager">Manager</option>
            </optgroup>
            <optgroup label="Sales Team">
              <option value="leader">Leader</option>
              <option value="agent">Agent</option>
              <option value="telemarketer">Telemarketer</option>
            </optgroup>
            <optgroup label="Design Team">
              <option value="sid">SID</option>
              <option value="asid">ASID</option>
              <option value="designer">Designer (3D)</option>
              <option value="draughter_2d">2D Draughter</option>
              <option value="draughter_site">Site Draughter</option>
            </optgroup>
            <optgroup label="Support Team">
              <option value="quotation_team">Quotation Team</option>
              <option value="legal_team">Legal Team</option>
            </optgroup>
            <optgroup label="Site Team">
              <option value="operation_head">Operation Head</option>
              <option value="site_manager">Site Manager</option>
              <option value="site_supervisor">Site Supervisor</option>
            </optgroup>
          </select>
        </div>

        {/* Manager Filter - for leaders */}
        {showManagerFilter && managers && managers.length > 0 && (
          <div>
            <label className="label-sm">Manager</label>
            <select
              name="managerId"
              value={filters.managerId || ''}
              onChange={handleChange}
              className="input"
            >
              <option value="">All Managers</option>
              {managers.map(manager => (
                <option key={manager.id} value={manager.id}>
                  {manager.nickname || manager.fullName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Leader Filter - for agents/telemarketers */}
        {showLeaderFilter && leaders && leaders.length > 0 && (
          <div>
            <label className="label-sm">Leader</label>
            <select
              name="leaderId"
              value={filters.leaderId || ''}
              onChange={handleChange}
              className="input"
            >
              <option value="">All Leaders</option>
              {leaders.map(leader => (
                <option key={leader.id} value={leader.id}>
                  {leader.nickname || leader.fullName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* SID Filter - for design team members */}
        {showSidFilter && sids && sids.length > 0 && (
          <div>
            <label className="label-sm">SID</label>
            <select
              name="sidId"
              value={filters.sidId || ''}
              onChange={handleChange}
              className="input"
            >
              <option value="">All SIDs</option>
              {sids.map(sid => (
                <option key={sid.id} value={sid.id}>
                  {sid.nickname || sid.fullName}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserFilters;
